import {
  useWSModal,
  WSButton,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useFieldArray, useFormContext } from "react-hook-form";
import { queriesPayees } from "@query";
import { useXWingspanPayerId } from "../../../modules/Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { ModalInvoiceSplitForm } from "./ModalInvoiceSplitForm";

export type InvoiceSplitValues = {
  payee: {
    payeeId: string;
    payeeEngagementId: string;
  } | null;
  description: string;
  amount: number | null;
  remove: string;
};

export type InvoiceSplitsSectionValues = {
  splits: InvoiceSplitValues[];
};

type Props = WSMarginProps & {
  disabled?: boolean;
  invoiceCurrency: Currency;
};

export const InvoiceSplitsSection: React.FC<Props> = props => {
  const { register, control } = useFormContext();
  const {
    fields: splits,
    append,
    remove,
    insert
  } = useFieldArray({
    control,
    name: "splits"
  });

  const modalForm = useWSModal(ModalInvoiceSplitForm, { size: "S" });

  return (
    <WSElement {...props}>
      <WSText.Heading5 mb="XS">Contractors</WSText.Heading5>
      <WSText color="gray600" mb="XL">
        Automatically route payments to subcontractors when invoice is paid.
      </WSText>

      <WSElement mt="XL" mb="XS">
        <WSGrid gutter="M">
          <WSGrid.Item span={{ xs: "6", s: "7" }}>
            <WSText.ParagraphSm color="gray500">Description</WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ xs: "6", s: "5" }}>
            <WSText.ParagraphSm color="gray500">Amount</WSText.ParagraphSm>
          </WSGrid.Item>
        </WSGrid>
      </WSElement>
      <WSDivider mb="M" />

      {splits.map((split, index) => (
        <WSElement my="M" key={split.id} hidden={split.remove}>
          <input
            type="hidden"
            name={`splits[${index}].payee.payeeId`}
            ref={register()}
            defaultValue={split.payee?.payeeId || ""}
          />
          <input
            type="hidden"
            name={`splits[${index}].payee.payeeEngagementId`}
            ref={register()}
            defaultValue={split.payee?.payeeEngagementId || ""}
          />
          <input
            type="hidden"
            name={`splits[${index}].description`}
            ref={register()}
            defaultValue={split.description || ""}
          />
          <input
            type="hidden"
            name={`splits[${index}].amount`}
            ref={register({ valueAsNumber: true })}
            defaultValue={split.amount || null}
          />
          <input
            type="hidden"
            name={`splits[${index}].remove`}
            ref={register()}
            defaultValue={split.remove || ""}
          />

          <WSGrid gutter="M">
            <WSGrid.Item span={{ xs: "6", s: "7" }}>
              {split.payee?.payeeId && (
                <PayeePreview payeeId={split.payee.payeeId} />
              )}
              <WSText.ParagraphSm color="gray600">
                {split?.description}
              </WSText.ParagraphSm>
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "4", s: "4" }}>
              <WSFlexBox.CenterY wrap="nowrap" style={{ height: "100%" }}>
                <WSText>
                  {formatMoney(split?.amount, props.invoiceCurrency)}
                </WSText>
              </WSFlexBox.CenterY>
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "2", s: "1" }}>
              <WSFlexBox.CenterY wrap="nowrap" style={{ height: "100%" }}>
                <WSButton.Link
                  size="M"
                  type="button"
                  disabled={props.disabled}
                  onClick={() => {
                    modalForm.open(
                      {
                        defaultValues: split as any,
                        invoiceCurrency: props.invoiceCurrency,
                        onSubmit: values => {
                          remove(index);
                          insert(index, values);
                        },
                        onDelete: () => {
                          remove(index);
                          insert(index, {
                            remove: "1"
                          });
                        }
                      },
                      {
                        title: "Edit invoice contractor"
                      }
                    );
                  }}
                  name={`editSplit${index}`}
                >
                  Edit
                </WSButton.Link>
              </WSFlexBox.CenterY>
            </WSGrid.Item>
          </WSGrid>
        </WSElement>
      ))}

      <WSButton.Link
        mb="S"
        size="M"
        type="button"
        disabled={props.disabled}
        icon="plus-circle"
        onClick={() => {
          modalForm.open(
            {
              invoiceCurrency: props.invoiceCurrency,
              onSubmit: values => {
                append(values);
              }
            },
            {
              title: "Add invoice contractor"
            }
          );
        }}
      >
        Add contractor
      </WSButton.Link>

      <WSDivider mb="M" />
    </WSElement>
  );
};

type PayeePreviewProps = {
  payeeId: string;
};

const PayeePreview = ({ payeeId }: PayeePreviewProps) => {
  const xWingspanAccount = useXWingspanPayerId();
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount
  });

  if (!queryPayee.data?.email) {
    return null;
  }

  return <WSText>{queryPayee.data.email}</WSText>;
};
